import {
  BufferSizeMap,
  SnapshotBase,
  SnapshotsGroupedByTrackId,
} from "@/shared/types";
import {
  Observable,
  ObservableObject,
  beginBatch,
  endBatch,
  observable,
} from "@legendapp/state";
import {
  findClosest,
  getDateTime,
  getTimestampIndex,
  groupConsecutiveNumbers,
  isSnapshotAlreadyExist,
} from "../utils";

import { TrackSourceBuffer } from "../track-source-buffer";
import { TrackBuffer } from "../types";
import { yieldToMain } from "@/shared";

const FRAMES_PER_SECOND = 30;
const STATIC_TRACK_ID = "unmatched-track-id";

export class SnapshotsSourceBuffer<
  TSnapshot extends SnapshotBase,
> extends TrackSourceBuffer<TSnapshot, TrackBuffer<TSnapshot>> {
  protected buffer: ObservableObject<TrackBuffer<TSnapshot>>;
  private timestampObject = new Date();
  bufferSizeMap: ObservableObject<BufferSizeMap>;
  isEmpty: Observable<boolean> = observable(true);

  constructor() {
    super();
    this.buffer = observable<TrackBuffer<TSnapshot>>({});
    this.bufferSizeMap = observable<BufferSizeMap>({});
  }

  private appendSnapshot(trackId: string, snapshot: TSnapshot) {
    const snapshotDate = new Date(getDateTime(snapshot));
    const snapshotGroupIndex = getTimestampIndex(
      snapshotDate,
      this.startTimeAnchor
    );
    const trackBuffer = this.buffer[trackId].peek();
    const snapshotGroup = trackBuffer.get(snapshotGroupIndex);

    if (isSnapshotAlreadyExist(snapshot, snapshotGroup)) return;

    if (snapshotGroup && snapshotGroup.length <= FRAMES_PER_SECOND) {
      snapshotGroup.push(snapshot);
    } else if (!snapshotGroup) {
      this.buffer[trackId].set(snapshotGroupIndex, [snapshot]);
    }

    if (this.isEmpty.peek()) {
      this.isEmpty.set(false);
    }
  }

  async appendBuffer(tracks: SnapshotsGroupedByTrackId<TSnapshot>[]) {
    let bucketSizeChanged = false;
    for (const track of tracks) {
      const trackId =
        track.track_id === null ? STATIC_TRACK_ID : track.track_id;

      const trackBuffer = this.buffer[trackId].get();
      if (!trackBuffer) {
        this.buffer[trackId].set(new Map());
      }
      const bucketSizeBefore = this.buffer[trackId].peek().size;
      for (const snapshot of track.snapshots) {
        this.appendSnapshot(trackId, snapshot);
        await yieldToMain();
      }
      bucketSizeChanged =
        bucketSizeChanged ||
        this.buffer[trackId].peek().size > bucketSizeBefore;
    }

    if (bucketSizeChanged) {
      this.groupBufferChunks();
    }
  }

  getBufferDateRange(buffer: Map<number, TSnapshot[]>): [number, number] {
    const buckets = Array.from(buffer.values());

    const firstSnapshot = buckets.at(0)?.at(0);
    const lastSnapshot = buckets.at(-1)?.at(-1);

    if (!firstSnapshot || !lastSnapshot) {
      return [NaN, NaN];
    }

    return [
      new Date(getDateTime(firstSnapshot)).getTime(),
      new Date(getDateTime(lastSnapshot)).getTime(),
    ];
  }

  findSnapshot(key: string, currentTime: number): TSnapshot | null {
    this.timestampObject.setTime(currentTime);
    const timestampIndex = getTimestampIndex(
      this.timestampObject,
      this.startTimeAnchor
    );

    const trackBuffer = this.buffer[key].peek();

    if (timestampIndex < 0) return null;
    const itemSnapshots = trackBuffer?.get(timestampIndex);

    if (!itemSnapshots) return null;

    const [start, end] = this.getBufferDateRange(trackBuffer);

    if (start !== end && (currentTime < start || currentTime > end)) {
      return null;
    }

    const snapshotIndex = findClosest(currentTime, itemSnapshots);
    return trackBuffer.get(timestampIndex)?.[snapshotIndex] ?? null;
  }

  getNextSnapshotDatetime(
    datetime: number,
    direction: "forward" | "backward"
  ): Date | null {
    const tracks = Object.keys(this.buffer.peek());

    let closestDatetime: Date | null = null;
    const currentDateTime = new Date(datetime);

    if (this.startTime && currentDateTime < this.startTime) {
      return null;
    }

    const timestampIndex = getTimestampIndex(
      new Date(datetime),
      this.startTimeAnchor
    );

    for (const trackId of tracks) {
      const trackBuffer = this.buffer[trackId].peek();

      if (timestampIndex < 0) continue;
      const snapshotGroup = trackBuffer?.get(timestampIndex);

      if (!snapshotGroup) continue;
      const snapshotIndex = findClosest(datetime, snapshotGroup);

      if (direction === "forward") {
        if (snapshotIndex < snapshotGroup.length - 1) {
          const snapshot = snapshotGroup[snapshotIndex + 1];
          const snapshotDateTime = new Date(getDateTime(snapshot));

          if (
            !closestDatetime ||
            snapshotDateTime.getTime() < closestDatetime.getTime()
          ) {
            closestDatetime = snapshotDateTime;
            continue;
          }
        } else {
          const nextSnapshotGroup = trackBuffer?.get(timestampIndex + 1);

          if (!nextSnapshotGroup) continue;

          const nextSnapshot = nextSnapshotGroup.at(0);
          const snapshotDateTime = new Date(
            getDateTime(nextSnapshot as TSnapshot)
          );

          if (
            !closestDatetime ||
            snapshotDateTime.getTime() < closestDatetime.getTime()
          ) {
            closestDatetime = snapshotDateTime;
            continue;
          }
        }
      } else {
        if (snapshotIndex > 0) {
          const snapshot = snapshotGroup[snapshotIndex - 1];
          const snapshotDateTime = new Date(getDateTime(snapshot));

          if (
            !closestDatetime ||
            snapshotDateTime.getTime() > closestDatetime.getTime()
          ) {
            closestDatetime = snapshotDateTime;
            continue;
          }
        } else {
          const nextSnapshotGroup = trackBuffer?.get(timestampIndex - 1);

          if (!nextSnapshotGroup) continue;

          const prevSnapshot = nextSnapshotGroup.at(-1); // get last item
          const snapshotDateTime = new Date(
            getDateTime(prevSnapshot as TSnapshot)
          );

          if (
            !closestDatetime ||
            snapshotDateTime.getTime() > closestDatetime.getTime()
          ) {
            closestDatetime = snapshotDateTime;
            continue;
          }
        }
      }
    }

    if (!closestDatetime) {
      const addition = direction === "forward" ? 40 : -40;
      const nextDateTime = this.getNextSnapshotDatetime(
        new Date(datetime).getTime() + addition,
        direction
      );

      if (nextDateTime) {
        return nextDateTime;
      }
    }

    return closestDatetime;
  }

  async flushBuffer(
    durationInSec: number,
    currentTime: number,
    onFlush?: () => void
  ) {
    const trackIds = Object.keys(this.buffer.peek());
    const currentTimeIndex = getTimestampIndex(
      new Date(currentTime),
      this.startTimeAnchor
    );

    for (const trackId of trackIds) {
      let counter = 0;

      const bufferSnapshots = this.buffer[trackId].peek();
      const buckets = Array.from(bufferSnapshots.keys());

      while (
        counter < durationInSec &&
        buckets[counter] < currentTimeIndex - 2
      ) {
        bufferSnapshots.delete(buckets[counter]);
        onFlush?.();
        counter++;
      }
    }
  }

  clear(): void {
    this.buffer.set({});
    this.isEmpty.set(true);
    this.bufferSizeMap.set({});
  }

  private updateBuffer(trackId: string) {
    const trackBuffer = this.buffer[trackId].peek();
    const snapshotGroups = Array.from(trackBuffer?.keys() || []);

    snapshotGroups.sort((a, b) => a - b);

    const trackCacheChunks = groupConsecutiveNumbers(snapshotGroups);
    this.bufferSizeMap[trackId].set(trackCacheChunks);
  }

  private async groupBufferChunks() {
    const buffer = this.buffer.peek();
    const tracks = Object.keys(buffer);

    beginBatch();
    for (const trackId of tracks) {
      this.updateBuffer(trackId);
      await yieldToMain();
    }
    endBatch();
  }
}
